import * as React from 'react';
import { escape } from '../../utils/component/helpers';
import { logCreativeClick, logCreativeLoad } from '../../utils/logger';
import { setUrlHttpsIfMissing } from '../../utils/string';
import { ContentMarketing as ContentMarketingT } from '../../../shared/types/contentMarketing';
import { useEffect, useMemo } from 'react';
import { getConfig } from '../../config';

type Props = {
    positionId: string;
    isFrontpage?: boolean;
    rowIndex?: number;
    contentMarketing: ContentMarketingT;
};

export function ContentMarketing(props: Props) {
    const {
        positionId,
        isFrontpage = false,
        rowIndex,
        contentMarketing,
    } = props;

    const isRow = useMemo(() => rowIndex !== undefined, [rowIndex]);

    const { adType, elementType } = useMemo(() => {
        return {
            adType: isRow ? 'Content' : 'Content',
            elementType: isRow
                ? rowIndex === 0
                    ? 'Split Left'
                    : 'Split Right'
                : undefined,
        };
    }, [isRow, rowIndex]);

    const { verticalName, subVertical } = useMemo(() => {
        const config = getConfig();
        return {
            verticalName: config.vertical,
            subVertical: config.subvertical,
        };
    }, []);

    useEffect(() => {
        if (document) {
            if (contentMarketing) {
                logCreativeLoad({
                    creativeId: contentMarketing.id,
                    elementId: positionId,
                    verticalName,
                    subVertical,
                    elementType,
                    adType,
                    adFormat: isRow ? 'Split-view' : 'Single-view',
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positionId]);

    if (contentMarketing) {
        const htmlContent = {
            escapedImgUrl: escape(contentMarketing.image.url),
            escapedIconUrl: escape(contentMarketing.icon.url),
            escapedSponsor: escape(contentMarketing.sponsor),
            escapedHref: escape(setUrlHttpsIfMissing(contentMarketing.href)),
            escapedTitle: escape(contentMarketing.title),
            escapedText: escape(contentMarketing.text),
        };

        const containerRowStyle = isRow ? 'flex-auto' : '';
        const articleFrontpageStyle = isFrontpage || isRow ? '' : 'mt-24 mx-16';
        const articleRowStyle = isRow ? 'h-full' : '';

        return (
            <div
                onClick={() =>
                    logCreativeClick({
                        creativeId: contentMarketing.id,
                        elementId: positionId,
                        verticalName,
                        subVertical,
                        targetId: htmlContent.escapedHref,
                        provider: htmlContent.escapedSponsor,
                        adType,
                        elementType,
                        adFormat: isRow ? 'Split-view' : 'Single-view',
                    })
                }
                id={positionId}
                className={containerRowStyle}
            >
                <article
                    className={`relative overflow-hidden transition-all outline-none sf-ad-outline sf-ad-card rounded-8 mb-auto ${articleFrontpageStyle} ${articleRowStyle}`}
                    tabIndex={0}
                >
                    <button className="sr-only" tabIndex={-1}>
                        Velg
                    </button>
                    <div aria-owns="title_id" />
                    {isRow ? (
                        <RowComponent htmlContent={htmlContent} />
                    ) : (
                        <ControlComponent htmlContent={htmlContent} />
                    )}
                </article>
            </div>
        );
    }
    return null;
}

export const MarketingLabel = ({
    escapedSponsor,
    escapedIconUrl,
    justifyBetween = true,
}) => (
    <div
        className={`flex w-full ${
            justifyBetween ? 'justify-between px-16' : ''
        } items-center bg-white py-8`}
    >
        <div className={justifyBetween ? 'text-16' : 'mr-8 text-16'}>
            Annonsørinnhold
        </div>
        <img alt={escapedSponsor} className="h-16" src={escapedIconUrl} />
    </div>
);

const ContentText = ({
    isRow,
    escapedSponsor = 'Testtest test',
    escapedHref,
    escapedTitle,
    escapedText = 'Testtest test',
}) => (
    <div>
        <div
            className={`m-16 ${!!escapedSponsor.length ? 'mb-8' : ''} ${
                isRow ? 'text-gray-500 text-14' : ''
            }`}
        >
            <span>{escapedSponsor}</span>
        </div>
        <h3
            className={`${
                isRow ? 'h4' : 'h2 md:h1'
            } font-normal text-gray-400 m-16 mt-8`}
            id="title_id"
        >
            <a
                style={{ textDecoration: 'none' }}
                className="link link--dark sf-ad-link text-gray-700 hover:no-underline focus:no-underline focus-ring"
                href={escapedHref}
                target="_blank"
                title={escapedTitle}
                rel="noreferrer"
            >
                <span aria-hidden="true" className="inset-0 absolute" />
                {escapedTitle}
            </a>
        </h3>
        {!!escapedText?.length && (
            <p
                className="text-14 text-gray-600 mb-4 ml-16 mb-16 mr-16"
                dangerouslySetInnerHTML={{ __html: escapedText }}
            />
        )}
    </div>
);

const ControlComponent = ({ htmlContent }) => (
    <>
        <MarketingLabel
            justifyBetween={true}
            escapedIconUrl={htmlContent.escapedIconUrl}
            escapedSponsor={htmlContent.escapedSponsor}
        />
        <img
            alt="Annonsebilde"
            className="h-290 w-full object-cover"
            src={htmlContent.escapedImgUrl}
        />
        <ContentText
            escapedSponsor={htmlContent.escapedSponsor}
            escapedTitle={htmlContent.escapedTitle}
            escapedHref={htmlContent.escapedHref}
            escapedText={htmlContent.escapedText}
            isRow={false}
        />
    </>
);

const RowComponent = ({ htmlContent }) => (
    <>
        <img
            alt="Annonsebilde"
            className="h-290 w-full object-cover"
            src={htmlContent.escapedImgUrl}
        />
        <ContentText
            escapedSponsor={htmlContent.escapedSponsor}
            escapedTitle={htmlContent.escapedTitle}
            escapedHref={htmlContent.escapedHref}
            escapedText={htmlContent.escapedText}
            isRow={true}
        />
    </>
);
